import { useMemo } from 'react';

import { MultiSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { TRUST_ENTITY_KINDS } from '@/modules/entities/entities.constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityKind, EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useGetHouseholdTrustEntitiesQuery } from '../graphql/GetHouseholdTrustEntities.generated';

interface UseTrustEntityOptionsResult {
  options: MultiSelectInputOption[];
  loading: boolean;
}

/**
 * Hook to get entity options for the "Created from" multi-select input.
 * Queries all trust and retirement account entities in the household.
 */
export function useTrustEntityOptions(): UseTrustEntityOptionsResult {
  const { householdId } = useHouseholdDetailsContext();

  const { data, loading } = useGetHouseholdTrustEntitiesQuery({
    variables: {
      householdId: householdId ?? '',
      kindIn: [...TRUST_ENTITY_KINDS, EntityKind.RetirementPersonalAccount],
      stage: EntityStage.Active,
    },
    skip: !householdId,
  });

  const options = useMemo<MultiSelectInputOption[]>(() => {
    if (!data) return [];

    const entities = getNodes(data.entities);
    return entities.map((entity) => ({
      value: entity.id,
      display: entity.subtype.displayName,
    }));
  }, [data]);

  return {
    options,
    loading,
  };
}
